<template>
  <div class="order-queue">
    <!-- La bande des files : seulement celles où quelque chose attend, rangées par qui agit ; les vides se replient -->
    <div class="order-queue__band">
      <div v-if="hasWaiting" class="order-queue__groups">
        <div v-for="group in bandGroups" :key="group.key" class="order-queue__group">
          <div class="order-queue__group-title">{{ $t(`onboarding.queue.group.${group.key}`) }}</div>
          <div class="order-queue__pills">
            <button
                v-for="step in group.steps" :key="step" type="button"
                class="order-queue__pill"
                :class="{ 'is-active': queue === step, 'is-empty': !countOf(step), 'is-alert': ALERT_QUEUES.includes(step) && countOf(step) }"
                :title="oldestOf(step) ? $t('onboarding.queue.oldest', { age: ageOf(oldestOf(step)) }) : null"
                @click="select(step)"
            >
              <v-icon small class="order-queue__pill-icon">{{ QUEUE_ICONS[step] }}</v-icon>
              <span class="order-queue__pill-text">
                <span class="order-queue__pill-label">{{ $t(`onboarding.queue.step.${step}`) }}</span>
                <span v-if="countOf(step) && oldestOf(step)" class="order-queue__pill-age" :class="ageClass(step, oldestOf(step))">
                  {{ $t('onboarding.queue.since', { age: ageOf(oldestOf(step)) }) }}
                </span>
              </span>
              <span class="order-queue__pill-count">{{ countOf(step) }}</span>
            </button>
          </div>
        </div>
      </div>
      <div v-else class="order-queue__all-clear">
        <v-icon color="green" class="mr-2">mdi-check-circle-outline</v-icon>
        <span>{{ $t('onboarding.queue.all-clear') }}</span>
      </div>

      <!-- À côté : les files vides (repliées) et l'historique, discrets -->
      <div class="order-queue__aside">
        <v-btn v-if="hasWaiting && emptySteps.length" x-small text class="order-queue__link" @click="showEmpty = !showEmpty">
          {{ showEmpty ? $t('onboarding.queue.hide-empty') : $tc('onboarding.queue.show-empty', emptySteps.length, { n: emptySteps.length }) }}
        </v-btn>
        <span v-if="hasWaiting && emptySteps.length" class="order-queue__sep" />
        <v-btn
            v-for="step in historySteps" :key="step" x-small text
            class="order-queue__link" :class="{ 'is-active': queue === step }"
            @click="select(step)"
        >
          <v-icon x-small left>{{ QUEUE_ICONS[step] }}</v-icon>{{ $t(`onboarding.queue.step.${step}`) }}
        </v-btn>
      </div>
    </div>

    <v-card v-if="showList" outlined class="order-queue__card">
      <!-- La file choisie : son nom, ce qu'elle veut dire (une ligne), puis les outils -->
      <div class="order-queue__head">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" :color="ALERT_QUEUES.includes(queue) ? 'error' : 'primary'">{{ QUEUE_ICONS[queue] }}</v-icon>
          <span class="order-queue__title">{{ $t(`onboarding.queue.step.${queue}`) }}</span>
          <span v-if="!loading" class="order-queue__total">{{ $tc('onboarding.queue.total', total, { n: total }) }}</span>
        </div>
        <div class="order-queue__explained" :title="$t(`onboarding.queue.explained.${queue}`)">{{ $t(`onboarding.queue.explained.${queue}`) }}</div>
      </div>

      <div class="order-queue__toolbar">
        <v-text-field
            v-model="filters.search" :placeholder="$t(searchHint)"
            prepend-inner-icon="mdi-magnify" dense outlined clearable hide-details
            class="order-queue__search"
            @keyup.enter="reload" @click:clear="filters.search = null; reload()"
        />
        <organization-select
            v-if="!embedded && queue !== 'consents'"
            v-model="filters.organizationId" :label="$t('organization.organization')" dense outlined clearable hide-details selection-with-no-extra
            class="order-queue__filter"
        />
        <v-select v-if="kindFilterable" v-model="filters.kind" :items="kindOptions" :label="$t('onboarding.commissioning.kind-label')" dense outlined clearable hide-details class="order-queue__filter" />
        <v-select
            v-if="TECHNICIAN_QUEUES.includes(queue) && technicians.length"
            v-model="filters.technicianId" :items="technicians" item-text="name" item-value="id"
            :label="$t('onboarding.commissioning.technician')" dense outlined clearable hide-details class="order-queue__filter"
        />
        <v-spacer />
        <span v-if="refreshedAt" class="text-caption text--secondary text-no-wrap">{{ $t('onboarding.queue.refreshed', { time: refreshedAt }) }}</span>
        <v-btn icon small :loading="loading" :title="$t('onboarding.queue.refresh')" @click="reload"><v-icon small>mdi-refresh</v-icon></v-btn>
      </div>
      <v-divider />

      <v-alert v-if="error" type="error" dense outlined class="ma-4">{{ error }}</v-alert>

      <!-- Un clic sur une ligne ouvre le tiroir : tout ce qu'il faut pour continuer, sans quitter la file -->
      <v-data-table
          :headers="headers" :items="items" :loading="loading" :options.sync="options" :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [25, 50, 100] }" :no-data-text="$t(`onboarding.queue.empty.${queue}`)"
          :item-class="rowClass" class="order-queue__table"
          @click:row="onRowClick"
      >
        <template #item.since="{ item }">
          <span :class="ageClass(queue, sinceOf(item))" :title="sinceOf(item) | formatDate">{{ ageOf(sinceOf(item)) }}</span>
        </template>

        <!-- ── Les commandes ── -->
        <template #item.who="{ item }">
          <template v-if="queue === 'registrations'">
            <div class="font-weight-medium">{{ registrationName(item) }}</div>
            <div class="text-caption text--secondary">{{ item.email }}<template v-if="!embedded"> · {{ organizationNames[item.organizationId] || '…' }}</template></div>
          </template>
          <template v-else-if="queue === 'consents'">
            <div class="font-weight-medium">{{ item.customer.name || item.customer.email || '-' }}</div>
            <div class="text-caption text--secondary">{{ (item.organization && item.organization.name) || '-' }}</div>
          </template>
          <template v-else>
            <div class="font-weight-medium">
              <router-link v-if="item.memberId" target="_blank" :to="{ name: 'member', params: { memberId: item.memberId } }">{{ beneficiaryOf(item) }}</router-link>
              <span v-else>{{ beneficiaryOf(item) }}</span>
            </div>
            <div v-if="!embedded" class="text-caption">
              <router-link target="_blank" :to="{ name: 'organizations.detail', params: { id: item.organizationId } }" class="text--secondary">{{ item.organizationName }}</router-link>
            </div>
          </template>
        </template>

        <template #item.what="{ item }">
          <template v-if="queue === 'consents'">
            {{ $t(`onboarding.consent-withdrawal.source.${item.source}`) }} · {{ $t(`onboarding.consent-withdrawal.purpose.${item.purpose}`) }}
            <div v-if="item.withdrawal.reason" class="text-caption text--secondary order-queue__quote">« {{ item.withdrawal.reason }} »</div>
          </template>
          <template v-else>
            <v-icon small class="mr-1">{{ kindIcon(item.kind) }}</v-icon>
            <a href="#" @click.prevent="openDrawer(item)">{{ kindLabel(item.kind) }}</a>
            <div v-if="item.kind === 'badge'" class="text-caption text--secondary">{{ supplyLabel(item) }}</div>
            <div v-else class="text-caption text--secondary">{{ $t('onboarding.queue.ordered-on', { date: dayOf(item.createdAt) }) }}</div>
          </template>
        </template>

        <template #item.requestedBy="{ item }">{{ requesterOf(item) }}</template>

        <template #item.missing="{ item }">
          <v-chip v-for="requirement in pendingRequirements(item).filter(r => PERSON_REQUIREMENTS.includes(r))" :key="requirement" x-small outlined class="mr-1 mb-1">
            {{ requirementLabel(requirement) }}
          </v-chip>
        </template>

        <template #item.reminded="{ item }">
          <span v-if="item.remindedAt">{{ dayOf(item.remindedAt) }}</span>
          <span v-else class="text--disabled">{{ $t('onboarding.queue.never-reminded') }}</span>
        </template>

        <template #item.address="{ item }">
          <span :class="{ 'error--text': !formatAddress(item.shippingAddress) }">{{ formatAddress(item.shippingAddress) || $t('onboarding.queue.no-address') }}</span>
          <div v-if="item.shippingAddressChangedAt">
            <v-chip x-small color="orange" text-color="white" class="mt-1">
              {{ $t('onboarding.badge-shipments.address-changed', { date: $options.filters.formatDate(item.shippingAddressChangedAt) }) }}
            </v-chip>
          </div>
        </template>

        <!-- Une saisie commencée et pas validée : elle se voit, quelqu'un doit la finir -->
        <template #item.entry="{ item }">
          <template v-if="heldCount(item)">
            <v-chip x-small :color="heldCount(item) === orderedCount(item) ? 'blue' : 'orange'" text-color="white">
              {{ $t('onboarding.queue.entry.held', { held: heldCount(item), total: orderedCount(item) }) }}
            </v-chip>
            <div class="text-caption text--secondary">{{ $t('onboarding.queue.entry.by', { by: item.badgesHeldBy || '-', date: $options.filters.formatDate(item.badgesHeldAt) }) }}</div>
          </template>
          <span v-else class="text--disabled">{{ $t('onboarding.queue.entry.none') }}</span>
        </template>

        <template #item.sent="{ item }">
          {{ sentAt(item) | formatDate }}
          <div class="text-caption text--secondary">{{ Object.values(item.designatedBadges || {}).join(' · ') }}</div>
        </template>

        <template #item.validation="{ item }">
          <div v-for="validation in (item.badgeValidations || []).filter(v => v.status === 'pending')" :key="validation.uid" class="text-no-wrap">
            <span class="font-weight-medium">{{ validation.uid }}</span>
            <span class="text-caption text--secondary"> · {{ $t(`onboarding.commissioning.badge-supply.${validation.kind}`) }}</span>
          </div>
        </template>
        <template #item.place="{ item }">{{ addressOf(item) }}</template>
        <template #item.desiredDate="{ item }">
          <span v-if="item.desiredDate">{{ dayOf(item.desiredDate) }}</span>
          <span v-else class="text--disabled">-</span>
        </template>
        <!-- Revenue « à planifier » depuis le terrain : le problème se voit sans ouvrir la commande -->
        <template #item.fieldIssue="{ item }">
          <v-chip v-if="item.fieldIssue && !item.fieldIssue.resolvedAt" x-small color="red" text-color="white" :title="fieldIssueLabel(item.fieldIssue)">
            <v-icon x-small left>mdi-account-hard-hat-outline</v-icon>{{ $t(`onboarding.field.issue.reason.${item.fieldIssue.reason}`) }}
          </v-chip>
          <span v-else class="text--disabled">-</span>
        </template>
        <template #item.technician="{ item }">
          <span v-if="item.technician">{{ item.technician.name }}</span>
          <span v-else class="text--disabled">-</span>
        </template>
        <template #item.scheduledAt="{ item }">
          <span v-if="item.scheduledAt">{{ item.scheduledAt | formatDate }}</span>
          <span v-else class="text--disabled">-</span>
        </template>
        <template #item.plan="{ item }">
          <order-plan-failure v-if="item.planSummary && item.planSummary.status === 'failed'" :order="item" dense />
          <span v-else-if="item.planSummary" class="text-caption">{{ $t(`onboarding.registration.plan-status.${item.planSummary.status}`) }}</span>
          <span v-else class="text--disabled">-</span>
        </template>
        <template #item.equipment="{ item }">{{ item.equipmentLabel || '-' }}</template>
        <template #item.closedAt="{ item }">{{ dayOf(item.closedAt) }}</template>
        <template #item.closing="{ item }">
          <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          <div v-if="item.closingReason" class="text-caption text--secondary">{{ item.closingReason }}</div>
        </template>

        <!-- ── Les inscriptions ── -->
        <template #item.source="{ item }">{{ $t(`onboarding.registration.source.${item.source}`) }}</template>
        <template #item.complete="{ item }">
          <v-chip v-if="item.completeForApproval" x-small color="green" text-color="white">{{ $t('onboarding.queue.registration.complete') }}</v-chip>
          <v-chip v-else x-small outlined>{{ $t('onboarding.queue.registration.incomplete') }}</v-chip>
        </template>

        <!-- Le geste de la file, et lui seul ; le reste se fait dans le tiroir -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-end align-center" style="gap: 6px">
            <order-approval-actions v-if="queue === 'approval' && mayManageOnboarding" :order="item" confirm @updated="refresh" />
            <order-reminder-action v-else-if="queue === 'person' && mayFollowOrders" :order="item" hide-date @updated="patch" />
            <v-btn v-else-if="queue === 'shipment'" small color="primary" :to="{ name: 'onboarding.badge-shipments.preparation', params: { id: item.id } }">
              <v-icon left small>mdi-package-variant</v-icon>{{ $t(heldCount(item) ? 'onboarding.queue.action.resume' : 'onboarding.queue.action.prepare') }}
            </v-btn>
            <v-btn v-else-if="queue === 'handover'" small color="primary" @click="handing = item">
              <v-icon left small>mdi-card-account-details-outline</v-icon>{{ $t(heldCount(item) ? 'onboarding.queue.action.resume' : 'onboarding.queue.action.hand-over') }}
            </v-btn>
            <registration-approval-actions
                v-else-if="queue === 'registrations' && item.completeForApproval && mayApproveMembers"
                :registration-id="item.id" :confirm-with="{ person: registrationName(item), organization: organizationNames[item.organizationId] || '' }"
                @decided="refresh"
            />
            <v-btn v-else-if="ROW_ACTIONS[queue]" small color="primary" @click="openDrawer(item)">
              <v-icon left small>{{ QUEUE_ICONS[queue] }}</v-icon>{{ $t(`onboarding.queue.action.${ROW_ACTIONS[queue]}`) }}
            </v-btn>
            <v-btn v-if="isOrderQueue(queue)" icon small target="_blank" :title="$t('onboarding.queue.open-new-tab')" :to="{ name: 'onboarding.commissionings.detail', params: { id: item.id } }">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Le tiroir : la ligne choisie, son panneau d'étape, et « Suivante » -->
    <order-drawer
        v-if="queue"
        v-model="drawer.open" :queue="queue" :item="drawer.item" :icon="QUEUE_ICONS[queue]" :position="drawerPosition"
        :has-next="hasNext" :technicians="technicians.length ? technicians : null" :organization-id="embedded ? organizationId : null"
        @next="openNext" @changed="onDrawerChanged"
    />

    <!-- Remettre les badges : chacun saisi et validé, puis la remise -->
    <badge-designation-dialog :value="!!handing" :order="handing" @input="handing = $event ? handing : null" @updated="patch" @done="refresh" />
  </div>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import ConsentWithdrawalRepository from '@repository/ConsentWithdrawalRepository'
import OrganizationRepository from '@repository/OrganizationRepository'
import RegistrationRepository from '@repository/RegistrationRepository'
import OrganizationSelect from '@blocks/Select/OrganizationSelect.vue'
import CommissioningMixin, { PERSON_REQUIREMENTS } from '../CommissioningMixin'
import RegistrationMixin from '../RegistrationMixin'
import BadgeDesignationDialog from '../Commissioning/BadgeDesignationDialog.vue'
import OrderApprovalActions from '../Order/OrderApprovalActions.vue'
import OrderPlanFailure from '../Order/OrderPlanFailure.vue'
import OrderReminderAction from '../Order/OrderReminderAction.vue'
import RegistrationApprovalActions from '../Registration/RegistrationApprovalActions.vue'
import OrderDrawer from './OrderDrawer.vue'
import { equipmentKinds } from '../Commissioning/policyCatalogue'

/*
 * Les files, rangées par qui agit : les étapes des commandes (OrderStep côté serveur), et deux files qui ne sont pas des
 * commandes mais attendent aussi ZEborne — les inscriptions à valider, les demandes de retrait de consentement.
 * L'opérateur (pleine page) traite presque tout ; le gestionnaire (dans son organisation) ne fait que valider et remettre
 * les badges : le reste est chez ZEborne et sur le terrain. L'historique ne se compte pas : il est à part.
 */
const OPERATOR_GROUPS = [
  { key: 'to-do', steps: ['registrations', 'approval', 'badge-validation', 'shipment', 'handover', 'survey', 'scheduling', 'commissioning', 'consents'] },
  { key: 'field', steps: ['installation'] },
  { key: 'waiting', steps: ['person', 'activation'] },
]
const MANAGER_GROUPS = [
  { key: 'to-you', steps: ['registrations', 'approval', 'handover'] },
  { key: 'zeborne', steps: ['badge-validation', 'shipment', 'survey', 'scheduling', 'installation', 'commissioning'] },
  { key: 'waiting', steps: ['person', 'activation'] },
]
const HISTORY_STEPS = ['done', 'closed']
/* Des colonnes qui ne disent rien quand aucune ligne de la page ne les remplit : on ne les montre pas. */
const OPTIONAL_COLUMNS = {
  fieldIssue: item => item.fieldIssue && !item.fieldIssue.resolvedAt,
  desiredDate: item => item.desiredDate,
  reminded: item => item.remindedAt,
  plan: item => item.planSummary,
  technician: item => item.technician,
  scheduledAt: item => item.scheduledAt,
  equipment: item => item.equipmentLabel,
}
const HISTORY = { done: ['in-service'], closed: ['rejected', 'cancelled'] }
const NOT_ORDERS = ['registrations', 'consents']
/* Les files de badges : un seul type d'équipement, le filtre par type n'y a pas de sens. */
const BADGE_STEPS = ['badge-validation', 'shipment', 'handover', 'activation']
const TECHNICIAN_QUEUES = ['scheduling', 'installation']
/* Une mise en service arrêtée est une anomalie : sa tuile et son explication passent en alerte. */
const ALERT_QUEUES = ['commissioning']
const QUEUE_ICONS = {
  registrations: 'mdi-clipboard-account-outline',
  approval: 'mdi-gavel',
  'badge-validation': 'mdi-card-search-outline',
  shipment: 'mdi-email-fast-outline',
  handover: 'mdi-hand-extended-outline',
  survey: 'mdi-clipboard-check-outline',
  scheduling: 'mdi-calendar-clock',
  commissioning: 'mdi-alert-octagon-outline',
  consents: 'mdi-shield-off-outline',
  installation: 'mdi-account-hard-hat-outline',
  person: 'mdi-account-clock-outline',
  activation: 'mdi-card-account-details-star-outline',
  done: 'mdi-check-circle-outline',
  closed: 'mdi-close-circle-outline',
}
/* Le geste d'une ligne qui se fait dans le tiroir (les autres ont leur bouton propre). */
const ROW_ACTIONS = { 'badge-validation': 'decide', survey: 'survey', scheduling: 'schedule', installation: 'follow', commissioning: 'look', consents: 'decide', registrations: 'look' }
/* Au-delà, l'attente se voit (jours) : orange, puis rouge. Chez la personne, on est plus patient. */
const LATE = { default: [2, 5], person: [7, 14], activation: [7, 14], installation: [7, 14] }
/* Une page de la file des inscriptions sert aussi au compteur : la plus ancienne s'y lit. */
const REGISTRATION_COUNT_PAGE = 100

/**
 * Le poste de l'opérateur : chaque commande est dans une seule file, celle de qui doit agir maintenant (l'étape que le
 * serveur tient à jour), la plus ancienne en tête ; les inscriptions à valider et les retraits de consentement à trancher
 * ont la leur. Chaque file a ses colonnes et son geste ; un clic sur une ligne ouvre le tiroir, où l'on continue (valider,
 * relancer, planifier, reprendre…) puis passe à la suivante. Ce qui attend depuis trop longtemps se voit en couleur.
 *
 * La file, les filtres et la ligne ouverte vivent dans l'adresse (`?queue=…&order=…`) : un lien ou un rechargement
 * rouvre le même tiroir. Pleine page (toutes les organisations du périmètre) ou dans une organisation (`embedded`).
 */
export default {
  name: 'OrderQueue',

  components: { OrganizationSelect, BadgeDesignationDialog, OrderApprovalActions, OrderPlanFailure, OrderReminderAction, RegistrationApprovalActions, OrderDrawer },

  mixins: [CommissioningMixin, RegistrationMixin],

  props: {
    organizationId: { type: String, default: null },
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      QUEUE_ICONS, PERSON_REQUIREMENTS, TECHNICIAN_QUEUES, ALERT_QUEUES, ROW_ACTIONS,
      queue: null,
      counts: {},
      items: [],
      total: 0,
      consentRows: [],
      organizationNames: {},
      options: { page: 1, itemsPerPage: 25 },
      loading: false,
      error: null,
      handing: null,
      drawer: { open: false, item: null, index: 0 },
      pendingDrawerId: null,
      requestedQueue: null,
      ready: false,
      technicians: [],
      kinds: [],
      refreshedAt: null,
      resettingPage: false,
      showEmpty: false,
      filters: { search: null, organizationId: null, kind: null, technicianId: null },
    }
  },

  computed: {
    scope() {
      return this.organizationId || this.filters.organizationId || null
    },

    permissions() {
      return this.currentUser?.permissions || []
    },

    /** Les files que ce lecteur peut ouvrir (hors historique) : les inscriptions avec un droit sur elles, les retraits pour ZEborne seul. */
    visibleGroups() {
      return (this.embedded ? MANAGER_GROUPS : OPERATOR_GROUPS)
        .map(group => ({ ...group, steps: group.steps.filter(step => this.isAvailable(step)) }))
        .filter(group => group.steps.length)
    },

    /** Dans la bande : les files où quelque chose attend, celle qu'on regarde, et les vides si on les a dépliées. */
    bandGroups() {
      return this.visibleGroups
        .map(group => ({ ...group, steps: group.steps.filter(step => this.showEmpty || this.countOf(step) || this.queue === step) }))
        .filter(group => group.steps.length)
    },

    emptySteps() {
      return this.visibleGroups.flatMap(group => group.steps).filter(step => !this.countOf(step))
    },

    historySteps() {
      return HISTORY_STEPS
    },

    hasWaiting() {
      return this.visibleGroups.some(group => group.steps.some(step => this.countOf(step)))
    },

    /** Rien n'attend : pas de tableau vide sous « Tout est à jour », sauf si l'on consulte l'historique. */
    showList() {
      return !!this.queue && (this.hasWaiting || !!HISTORY[this.queue])
    },

    kindFilterable() {
      return this.isOrderQueue(this.queue) && !BADGE_STEPS.includes(this.queue) && this.kinds.length > 1
    },

    kindOptions() {
      return this.kinds.map(value => ({ value, text: this.kindLabel(value) }))
    },

    searchHint() {
      if (this.queue === 'registrations') return 'onboarding.registration.search-hint'
      if (this.queue === 'consents') return 'onboarding.queue.consent-search-hint'
      return 'onboarding.commissioning.search-hint'
    },

    headers() {
      const h = (value, key, extra = {}) => ({ text: this.$t(`onboarding.queue.column.${key || value}`), value, sortable: false, class: 'text-no-wrap', ...extra })
      const columns = {
        registrations: [h('source'), h('complete')],
        approval: [h('requestedBy')],
        'badge-validation': [h('validation')],
        person: [h('missing'), h('reminded')],
        shipment: [h('address'), h('entry')],
        handover: [h('entry')],
        activation: [h('sent', `sent-${this.queue}`)],
        survey: [h('place'), h('desiredDate')],
        scheduling: [h('fieldIssue'), h('place'), h('desiredDate')],
        installation: [h('technician'), h('scheduledAt'), h('place'), h('plan')],
        commissioning: [h('plan')],
        consents: [],
        done: [h('equipment'), h('closedAt', 'in-service-at')],
        closed: [h('closing'), h('closedAt')],
      }[this.queue] || []
      const shown = columns.filter(column => !OPTIONAL_COLUMNS[column.value] || this.items.some(OPTIONAL_COLUMNS[column.value]))

      return [
        ...(HISTORY[this.queue] ? [] : [h('since', null, { width: 130 })]),
        h('who'),
        h('what', this.queue === 'consents' ? 'consent' : null),
        ...shown,
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ]
    },

    /** La ligne d'après celle du tiroir ; si elle a quitté la file (traitée), celle qui a pris sa place. */
    nextItem() {
      if (!this.drawer.item) return null
      const index = this.items.findIndex(item => item.id === this.drawer.item.id)
      return index >= 0 ? (this.items[index + 1] || null) : (this.items[this.drawer.index] || null)
    },

    hasMorePages() {
      const { page = 1, itemsPerPage = 25 } = this.options
      return page * itemsPerPage < this.total
    },

    hasNext() {
      return !!this.nextItem || this.hasMorePages
    },

    drawerPosition() {
      if (!this.drawer.item || !this.total) return null
      const index = this.items.findIndex(item => item.id === this.drawer.item.id)
      if (index < 0) return null
      const { page = 1, itemsPerPage = 25 } = this.options
      return this.$t('onboarding.queue.drawer.position', { n: (page - 1) * itemsPerPage + index + 1, total: this.total })
    },
  },

  watch: {
    options: {
      handler(next, previous) {
        // Remis à la première page par l'écran lui-même : le chargement est déjà parti.
        if (this.resettingPage) {
          this.resettingPage = false
          return
        }
        if (previous && (next.page !== previous.page || next.itemsPerPage !== previous.itemsPerPage)) this.loadItems()
      },
      deep: true,
    },
    'filters.organizationId'() {
      this.reload()
    },
    'filters.kind'() {
      this.reload()
    },
    'filters.technicianId'() {
      this.loadItems()
      this.remember()
    },
    'drawer.open'(open) {
      if (!open) this.drawer.item = null
      this.remember()
    },
    // L'utilisateur connecté peut arriver après l'écran (rechargement de page) : ses droits ouvrent alors les files
    // des inscriptions et des retraits, et la liste des techniciens.
    mayManageOnboarding(may, before) {
      if (may && !before) this.loadTechnicians()
    },
    permissions(now, before) {
      if (!this.ready || (before && before.length)) return
      if (this.requestedQueue && this.requestedQueue !== this.queue && this.isAvailable(this.requestedQueue)) this.select(this.requestedQueue)
      this.loadCounts()
    },
  },

  async created() {
    // Dans une organisation, l'adresse n'est pas la nôtre : on n'y lit que la file et la commande à ouvrir (un lien
    // depuis la fiche de l'organisation), sans jamais la réécrire.
    const query = this.embedded ? { queue: this.$route.query.queue, order: this.$route.query.order } : this.$route.query
    this.filters = { ...this.filters, search: query.search || null, organizationId: query.org || null, kind: query.kind || null, technicianId: query.tech || null }
    this.requestedQueue = query.queue && QUEUE_ICONS[query.queue] ? query.queue : null
    this.queue = this.requestedQueue && this.isAvailable(this.requestedQueue) ? this.requestedQueue : null
    this.pendingDrawerId = query.order || null
    this.loadTechnicians()
    this.kinds = (await equipmentKinds().catch(() => ({ all: [] }))).all
    await this.reload()
    this.ready = true
    this.reopenFromAddress()
  },

  methods: {
    async loadTechnicians() {
      if (this.mayManageOnboarding && !this.technicians.length) this.technicians = await CommissioningRepository.technicians().catch(() => [])
    },

    isOrderQueue(queue) {
      return !!queue && !NOT_ORDERS.includes(queue)
    },

    isAvailable(step) {
      if (step === 'registrations') return ['ONBOARDING_MODULE_MANAGE', 'MEMBER_APPROVE', 'ONBOARDING_INVITE'].some(p => this.permissions.includes(p))
      // ZEborne seul tranche, toutes organisations confondues : pas dans la file d'une organisation.
      if (step === 'consents') return !this.embedded && this.mayManageOnboarding && !!this.currentUser?.isZE
      return true
    },

    /** Les compteurs puis la file choisie — la première où quelqu'un d'ici doit agir, si rien n'est choisi. */
    async reload() {
      await this.loadCounts()
      // Rien de choisi : la première file où quelque chose attend, dans l'ordre de la bande (ce qui revient à ce
      // lecteur d'abord) ; sinon la première file de commandes, pour l'adresse.
      if (!this.queue) {
        const steps = this.visibleGroups.flatMap(group => group.steps)
        this.queue = steps.find(step => this.countOf(step)) || steps.find(step => this.isOrderQueue(step))
      }
      this.firstPage()
      await this.loadItems()
      this.remember()
    },

    /** Après un geste : les compteurs et la page en cours, sans revenir en tête de file. */
    async refresh() {
      await this.loadCounts()
      await this.loadItems()
    },

    async loadCounts() {
      this.error = null
      const search = this.filters.search
      const tasks = [
        CommissioningRepository.steps({ organizationId: this.scope, kind: this.queueKind(), search }).then(({ steps }) => steps || {}),
      ]
      if (this.isAvailable('registrations')) {
        tasks.push(RegistrationRepository.list({ organizationId: this.scope, status: ['awaiting-approval'], search, offset: 0, limit: REGISTRATION_COUNT_PAGE })
          .then(data => {
            // Seules comptent celles que la personne a finies : les autres l'attendent, elle.
            const complete = (data.items || []).filter(item => item.completeForApproval)
            return { registrations: { count: data.awaitingApproval || 0, oldestSince: this.oldest(complete.map(item => item.lastActivityAt)) } }
          }))
      }
      if (this.isAvailable('consents')) {
        tasks.push(ConsentWithdrawalRepository.list(false).then(rows => {
          this.consentRows = rows || []
          const matching = this.matchingConsents()
          return { consents: { count: matching.length, oldestSince: this.oldest(matching.map(row => row.withdrawal.requestedAt)) } }
        }))
      }
      const results = await Promise.all(tasks.map(task => task.catch(e => {
        this.error = this.messageOf(e)
        return {}
      })))
      this.counts = Object.assign({}, ...results)
      this.refreshedAt = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    async loadItems() {
      this.loading = true
      this.error = null
      const { page = 1, itemsPerPage = 25 } = this.options
      const offset = (page - 1) * itemsPerPage
      try {
        if (this.queue === 'registrations') {
          const data = await RegistrationRepository.list({ organizationId: this.scope, status: ['awaiting-approval'], search: this.filters.search, offset, limit: itemsPerPage })
          this.items = data.items || []
          this.total = data.total || 0
          this.nameOrganizations(this.items.map(item => item.organizationId))
        } else if (this.queue === 'consents') {
          const rows = this.matchingConsents()
          this.items = rows.slice(offset, offset + itemsPerPage)
          this.total = rows.length
        } else {
          const history = HISTORY[this.queue]
          const data = await CommissioningRepository.list({
            organizationId: this.scope,
            kind: this.queueKind(),
            search: this.filters.search,
            technicianId: TECHNICIAN_QUEUES.includes(this.queue) ? this.filters.technicianId : null,
            ...(history ? { status: history } : { step: [this.queue] }),
            offset,
            limit: itemsPerPage,
          })
          this.items = data.items || []
          this.total = data.total || 0
        }
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    /** Les demandes de retrait que la recherche désigne (la liste n'est pas paginée côté serveur : elle reste courte). */
    matchingConsents() {
      const search = (this.filters.search || '').trim().toLowerCase()
      if (!search) return this.consentRows
      return this.consentRows.filter(row => [row.customer?.name, row.customer?.email, row.organization?.name]
        .some(value => (value || '').toLowerCase().includes(search)))
    },

    /** Les organisations des inscriptions, par leur nom : la liste ne les nomme pas, on les lit une fois chacune. */
    nameOrganizations(ids) {
      if (this.embedded) return
      ;[...new Set(ids)].filter(id => id && !(id in this.organizationNames)).forEach(id => {
        this.organizationNames = { ...this.organizationNames, [id]: null }
        OrganizationRepository.get(id, true)
          .then(organization => { this.organizationNames = { ...this.organizationNames, [id]: organization?.name || '-' } })
          .catch(() => { this.organizationNames = { ...this.organizationNames, [id]: '-' } })
      })
    },

    firstPage() {
      this.resettingPage = true
      this.options = { ...this.options, page: 1 }
    },

    select(step) {
      if (this.queue === step) return
      this.queue = step
      this.items = []
      this.drawer = { open: false, item: null, index: 0 }
      this.firstPage()
      this.loadItems()
      this.remember()
    },

    /** La file, les filtres et la ligne ouverte dans l'adresse : un lien partagé ou un rechargement rouvre la même vue. */
    remember() {
      if (this.embedded) return
      const query = { queue: this.queue }
      if (this.filters.search) query.search = this.filters.search
      if (this.filters.organizationId) query.org = this.filters.organizationId
      if (this.filters.kind) query.kind = this.filters.kind
      if (this.filters.technicianId && TECHNICIAN_QUEUES.includes(this.queue)) query.tech = this.filters.technicianId
      if (this.drawer.open && this.drawer.item) query.order = this.drawer.item.id
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {})
    },

    /** Le tiroir de l'adresse : la ligne de la page s'il y est, sinon la commande (ou l'inscription) par son identifiant. */
    reopenFromAddress() {
      const id = this.pendingDrawerId
      this.pendingDrawerId = null
      if (!id) return
      const found = this.items.find(item => item.id === id)
      if (found) return this.openDrawer(found)
      // Une demande de retrait ne se relit pas seule : hors de la page, on n'a rien à montrer.
      if (this.queue !== 'consents') this.openDrawer({ id })
    },

    onRowClick(item, data, event) {
      // Un lien, un bouton ou un champ de la ligne fait son propre geste.
      if (event?.target?.closest && event.target.closest('a, button, input, textarea, .v-input, .v-chip--clickable')) return
      this.openDrawer(item)
    },

    openDrawer(item) {
      const index = this.items.findIndex(row => row.id === item.id)
      this.drawer = { open: true, item, index: index >= 0 ? index : 0 }
      this.remember()
    },

    async openNext() {
      const next = this.nextItem
      if (next) return this.openDrawer(next)
      if (!this.hasMorePages) return
      this.options = { ...this.options, page: (this.options.page || 1) + 1 }
      this.resettingPage = true
      await this.loadItems()
      if (this.items.length) this.openDrawer(this.items[0])
    },

    /** Un geste dans le tiroir : la file se recompte ; une décision qui fait quitter la ligne passe à la suivante. */
    async onDrawerChanged(event) {
      await this.refresh()
      if (event?.left) {
        if (this.nextItem) this.openDrawer(this.nextItem)
        else this.drawer.open = false
      }
    },

    queueKind() {
      return BADGE_STEPS.includes(this.queue) || !this.isOrderQueue(this.queue) ? null : this.filters.kind
    },

    isCounted(step) {
      return !HISTORY[step]
    },

    countOf(step) {
      return this.counts[step]?.count || 0
    },

    oldestOf(step) {
      return this.counts[step]?.oldestSince || null
    },

    oldest(dates) {
      return dates.filter(Boolean).sort()[0] || null
    },

    /** Depuis quand la ligne attend : à l'étape (commande), depuis la fin de sa part (inscription), depuis la demande. */
    sinceOf(item) {
      if (this.queue === 'registrations') return item.lastActivityAt
      if (this.queue === 'consents') return item.withdrawal?.requestedAt
      return item.stepSince
    },

    /** « 3 h », « 5 j » : depuis quand. */
    ageOf(date) {
      if (!date) return '-'
      const hours = Math.max(0, (Date.now() - new Date(date).getTime()) / 3600000)
      if (hours < 1) return this.$t('onboarding.queue.age.now')
      if (hours < 24) return this.$t('onboarding.queue.age.hours', { n: Math.floor(hours) })
      return this.$t('onboarding.queue.age.days', { n: Math.floor(hours / 24) })
    },

    ageClass(step, date) {
      if (!date || HISTORY[step]) return ''
      const days = (Date.now() - new Date(date).getTime()) / 86400000
      const [late, veryLate] = LATE[step] || LATE.default
      if (days >= veryLate) return 'order-queue__age--very-late'
      if (days >= late) return 'order-queue__age--late'
      return ''
    },

    rowClass(item) {
      return this.drawer.open && this.drawer.item && this.drawer.item.id === item.id ? 'order-queue__row is-open' : 'order-queue__row'
    },

    supplyLabel(order) {
      const key = `onboarding.commissioning.badge-supply.${order.options?.supply}`
      return this.$te(key) ? this.$t(key) : ''
    },

    requesterOf(order) {
      const r = order.requestedBy || {}
      return r.name || this.$t(`onboarding.queue.requester.${r.type}`)
    },

    orderedCount(order) {
      return (order.orderedBadgeKinds || []).length || 1
    },

    heldCount(order) {
      return Object.values(order.designatedBadges || {}).filter(Boolean).length
    },

    sentAt(order) {
      return (order.requirements || []).find(r => ['shipment', 'handover'].includes(r.requirement))?.satisfiedAt || null
    },

    /** Une commande à jour (un badge enregistré, une relance) : la ligne suit sans tout recharger. */
    patch(order) {
      this.items = this.items.map(item => (item.id === order.id ? { ...item, ...order } : item))
      if (this.handing && this.handing.id === order.id) this.handing = { ...this.handing, ...order }
    },
  },
}
</script>

<style scoped>
.order-queue {
  max-width: 1440px;
}
/* La bande : les groupes à gauche, les liens discrets (vides, historique) à droite */
.order-queue__band {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 16px;
}
.order-queue__groups {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
}
.order-queue__group-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 6px;
}
.order-queue__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.order-queue__pill {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 8px 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.order-queue__pill:hover {
  border-color: rgba(0, 0, 0, 0.3);
}
.order-queue__pill.is-active {
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 1px var(--v-primary-base) inset;
  background: rgba(229, 60, 34, 0.04);
}
.order-queue__pill.is-active .order-queue__pill-icon {
  color: var(--v-primary-base) !important;
}
.order-queue__pill.is-empty {
  opacity: 0.55;
}
.order-queue__pill.is-alert:not(.is-active) {
  border-color: #e57373;
  background: #fff6f6;
}
.order-queue__pill-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}
.order-queue__pill-label {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}
.order-queue__pill-age {
  font-size: 11px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}
.order-queue__pill-count {
  min-width: 26px;
  height: 26px;
  padding: 0 7px;
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 14px;
  font-weight: 700;
  line-height: 26px;
  text-align: center;
}
.order-queue__pill.is-active .order-queue__pill-count {
  background: #37474f;
  color: #fff;
}
/* L'alerte garde son rouge, choisie ou non : elle ne se confond pas avec la sélection */
.order-queue__pill.is-alert .order-queue__pill-count {
  background: #e53935;
  color: #fff;
}
.order-queue__all-clear {
  display: flex;
  align-items: center;
  font-size: 15px;
  padding: 10px 0;
}
.order-queue__aside {
  display: flex;
  align-items: center;
  gap: 2px;
}
.order-queue__link {
  text-transform: none;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.6) !important;
}
.order-queue__link.is-active {
  color: var(--v-primary-base) !important;
  font-weight: 600;
}
.order-queue__sep {
  width: 1px;
  height: 14px;
  background: rgba(0, 0, 0, 0.15);
  margin: 0 6px;
}
/* La file choisie : nom, une ligne d'explication, outils, puis le tableau */
.order-queue__head {
  padding: 14px 16px 0;
}
.order-queue__title {
  font-size: 15px;
  font-weight: 600;
}
.order-queue__total {
  margin-left: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
.order-queue__explained {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.order-queue__explained::first-letter {
  text-transform: uppercase;
}
.order-queue__toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px 14px;
}
.order-queue__search {
  flex: 0 1 340px;
}
.order-queue__filter {
  flex: 0 0 210px;
}
.order-queue__age--late {
  color: #ef6c00 !important;
  font-weight: 600;
}
.order-queue__age--very-late {
  color: #d32f2f !important;
  font-weight: 700;
}
.order-queue__table ::v-deep th {
  white-space: nowrap;
}
.order-queue__table ::v-deep tr.order-queue__row {
  cursor: pointer;
}
.order-queue__table ::v-deep tr.order-queue__row.is-open {
  background: rgba(229, 60, 34, 0.06);
}
.order-queue__quote {
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
